import React, { Component } from 'react';
import {
  arrayMove,
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import {
  Button,
  ButtonGroup,
  Modal,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  useTranslation,
  withTranslation,
  Translation,
  Trans,
} from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import {
  debounce,
  cloneDeep,
  every,
  isEmpty,
  omit,
  pick,
  sortBy,
  uniqBy,
} from 'lodash';
import { titleize, unescapeHTML } from 'underscore.string';
import moment from 'moment-timezone';
import phoneFormatter from 'phone-formatter';
import unitConverter from 'convert-units';

import { getClassName } from 'utils/ui/classes';
import Container from 'components/v2/container';
import CargomaticAlert from 'components/ui/Alert';
import CargomaticCard from 'components/v2/card';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import CargomaticMap from 'components/v2/map';
import CargomaticBadge from 'components/v2/Badge';
import CargomaticTooltip from 'components/v2/Tooltip';
import IconTextDisplay from 'components/v2/IconTextDisplay';
import AddButton from 'components/v2/buttons/AddButton';
import UserIcon from 'components/v2/icons/UserIcon';
import TruckIcon from 'components/v2/icons/TruckIcon';
import ClipboardListIcon from 'components/v2/icons/ClipboardListIcon';
import CircleArrowRightIcon from 'components/v2/icons/CircleArrowRightIcon';
import CircleArrowLeftIcon from 'components/v2/icons/CircleArrowLeftIcon';
import CircleCheckIcon from 'components/v2/icons/CircleCheckIcon';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import AccordionSection from 'components/ui/AccordionSectionV2';
import LoadingCircle from 'components/ui/LoadingCircle';
import CustomerSearch from 'components/site/customer-search';
import Spinner from 'components/ui/Spinner';
import EditReferenceNumbersModal from 'components/modules/shipments/EditReferenceNumbersModal';

import CustomersActionCreators from 'actions/CustomersActionCreators';
import MarketsAPIUtils from 'utils/MarketsAPIUtils';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import DocumentUploadStore from 'stores/DocumentUploadStore';
import LocationStore from 'stores/LocationStore';
import SessionStore from 'stores/SessionStore';
import { showToast } from 'stores/ToastStore';

import {
  TRAILER,
  getEquipmentTypesForShipmentTypes,
} from 'constants/EquipmentTypes';
import SHIPMENT_TYPES from 'constants/ShipmentTypes';
import {
  BOOKING_ACCESSORIALS,
  BOOKING_ACCESSORIALS_MAP,
} from 'config/accessorials';

import { fakeMongoObjectId } from 'helpers/helper_functions';
import Multiselect from 'components/v2/multi-select';
import {
  validateLoadDetailsV2,
  validateShipperReferenceNumbers,
} from 'helpers/booking/validation';
import { getShipmentDetailUrl } from 'utils/formatters/shipments';
import { getTimezones } from 'utils/Timezones';
import LoadDetailsForm from 'components/load/LoadDetailsForm';
import { getLoadText } from 'components/load/Loads';
import { DistanceUnitFormatter } from 'components/modules/master/formatters';
import { defaultLoad, DEFAULT_WEIGHT_UNIT } from 'utils/localization/types';
import { withLocaleContext } from 'providers/locale-settings-context';
import formatMoney from 'utils/formatters/Money';
import i18next from 'i18next';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import USER_TYPES from 'constants/UserTypes';
import { isValid, getAbbreviatedTimezone } from '../BookingHelper';
import FSCDisclaimer from '../../pricing/fsc-disclaimer';

import Stop from './Stop';
import AccessorialGrid from './AccessorialGrid';
import AccessorialUpload from './AccessorialUpload';
import StopSummaryItem from './StopSummaryItem';
import { BookingDeliveryOrder } from './BookingDeliveryOrder';
import { attachDocuments } from '../../../../utils/DocumentsAPIUtils';
// Shipment fields that are also reference numbers
const REFNUMS = ['tracking_id', 'mo_number', 'house_bill_number'];

// Km for Thai and Miles for US Command
const MAX_DISTANCE_MILES = 150;
const MAX_DISTANCE_MILES_SAME_DAY = 80;
const MIN_WINDOW_SIZE = 2;

const adminStepNumbers = {
  dispatchDetails: 1,
  loadDetails: 2,
  accessorials: 3,
  routeDetails: 4,
  review: 5,
};

const shipperStepNumbers = {
  dispatchDetails: 0,
  loadDetails: 1,
  accessorials: 2,
  routeDetails: 3,
  review: 4,
};

const stepNumbers = SessionStore.isAdmin()
  ? adminStepNumbers
  : shipperStepNumbers;

const getInitialState = (localeData) => {
  let accessorials = SessionStore.isAdmin()
    ? BOOKING_ACCESSORIALS
    : BOOKING_ACCESSORIALS.filter((acc) => acc.shipper_can_book);
  accessorials = sortBy(accessorials, 'description').map((acc) => {
    return { ...acc, selected: false };
  });
  DocumentUploadStore.clearDocuments();
  const pickupMinutes = moment().minute() > 30 ? 30 : 0;
  const pickup_start = moment(
    moment()
      .add(1, 'hour')
      .startOf('hour')
      .add(pickupMinutes, 'minutes')
      .format(),
  )
    .tz(DEFAULT_TIMEZONE)
    .format();
  const pickup_end = moment(moment(pickup_start).add(3, 'hours').format())
    .tz(DEFAULT_TIMEZONE)
    .format();
  const deliver_end = moment(moment(pickup_end).add(3, 'hours').format())
    .tz(DEFAULT_TIMEZONE)
    .format();
  const user = SessionStore.getUser() || {};
  const isShipper = SessionStore.isShipper();
  // Used to prevent shippers from accessing booking flow
  const hasAccountingHold =
    isShipper && user.accounting ? user.accounting.on_hold : false;

  return {
    showFSCDisclaimer: false,
    offer: null,
    booking_error: null,
    booking: false,
    shipper: null,
    hasAccountingHold,
    showAccountingHoldModal: hasAccountingHold,
    stops: [
      {
        sequence: 1,
        action: 'pickup',
        window_start: pickup_start,
        window_end: pickup_end,
        location: [{ address: '', contact: {} }],
        id: fakeMongoObjectId(),
        appointment_required: false,
      },
      {
        sequence: 2,
        action: 'deliver',
        window_start: pickup_end,
        window_end: deliver_end,
        location: [{ address: '', contact: {} }],
        id: fakeMongoObjectId(),
        appointment_required: false,
      },
    ],
    loads: [
      {
        ...defaultLoad,
        ...localeData,
      },
    ],
    weight: 0,
    weight_units: localeData.weight_units || DEFAULT_WEIGHT_UNIT,
    notes: '',
    equipment_type: [],
    full_truckload: false,
    project: false,
    hold_at_dispatch: false,
    exception_exempt: false,
    business: false,
    locations: [],
    tracking_id: '',
    mo_number: '',
    house_bill_number: '',
    reference_numbers: [],
    shipment_type: 'shorthaul',
    doName: '',
    distance: 0,
    price: 0,
    userTimezone: DEFAULT_TIMEZONE,
    showConfirmDeleteLocationModal: false,
    locationToDelete: null,
    step: 1,
    completedSteps: {},
    errors: {},
    accessorials,
    airport_pickup_docs: {},
    airport_delivery_docs: {},
    cfs_pickup_docs: {},
    cfs_delivery_docs: {},
    cfs_docs: {},
    airport_pickup: false,
    airport_delivery: false,
    cfs_pickup: false,
    cfs_delivery: false,
    cfs: false,
    distanceFetched: false,
    showDebug: false,
    deliveryOrderDocument: null,
    accessorialDocumentsAirportPickup: [],
    accessorialDocumentsAirportDelivery: [],
    accessorialDocumentsCfsPickup: [],
    accessorialDocumentsCfsDelivery: [],
    accessorialDocumentsCFS: [],
    selectedTimezone: DEFAULT_TIMEZONE,
    nextDisabled: false,
    docsRequiredAccessorials: [
      'airport_pickup',
      'airport_delivery',
      'cfs_pickup',
      'cfs_delivery',
    ],
  };
};

const addDefaultLoad = (localeData) => {
  return [
    {
      ...defaultLoad,
      ...localeData,
    },
  ];
};

function renderErrors(errors, title) {
  if (Object.keys(errors).length === 0) {
    return null;
  }
  return (
    <Translation>
      {(t) => (
        <CargomaticAlert
          className="mb-4"
          type="error"
          title={t(title) || t('ADDRESS_ISSUES')}
        >
          <ul className="ml-4 list-disc space-y-2">
            {Object.keys(errors).map((key) => (
              <li key={key}>{errors[key]}</li>
            ))}
          </ul>
        </CargomaticAlert>
      )}
    </Translation>
  );
}

function renderWarnings(warnings, title) {
  if (Object.keys(warnings).length === 0) {
    return null;
  }
  return (
    <Translation>
      {(t) => (
        <CargomaticAlert
          className="mb-4"
          type="warning"
          title={t(title) || t('WARNING')}
        >
          <ul className="ml-4 list-disc space-y-2">
            {Object.keys(warnings).map((key) => (
              <li key={key}>{warnings[key]}</li>
            ))}
          </ul>
        </CargomaticAlert>
      )}
    </Translation>
  );
}

const DragHandle = SortableHandle((props) => {
  if (props && props.disabled) {
    return <div />;
  }
  const { t } = useTranslation();
  const tooltip = (
    <Tooltip id="drag-stop-tooltip">{t('DRAG_TO_REORDER')}</Tooltip>
  );
  const isPickup = props.action === 'pickup';
  return (
    <OverlayTrigger placement="top" overlay={tooltip}>
      <div
        className={`cursor-pointer flex flex-row text-green ${
          isPickup ? 'text-xl' : 'text-2xl'
        }`}
      >
        <i className="fa fa-ellipsis-v" />
        <i className={`fa ${isPickup ? 'fa-circle-o' : 'fa-circle'}`} />
      </div>
    </OverlayTrigger>
  );
});

const SortableStop = SortableElement((props) => {
  return (
    <Stop {...props}>
      <DragHandle {...props.stop} />
    </Stop>
  );
});

const SortableStopsList = SortableContainer((props) => {
  const { t } = useTranslation();
  const stops = props.stops;
  return (
    <div className="">
      <div className="grid lg:grid-cols-12 lg:gap-x-4 lg:gap-y-6">
        <div className="lg:col-span-1">&nbsp;</div>
        <div className="text-sm font-medium text-gray-500 lg:col-span-2">
          {titleize(t('TYPE'))}
        </div>
        <div className="text-sm font-medium text-gray-500 lg:col-span-5">
          {titleize(t('LOCATION'))}
        </div>
        <div className="text-sm text-gray-500 lg:col-span-4 font-medimum">
          {titleize(t('APPOINTMENT_AND_LOADS'))}
        </div>
      </div>
      <div className="mt-2">
        {stops.map((s, i) => {
          return (
            <SortableStop
              key={s.sequence}
              index={i}
              stop={s}
              showTotalWeight={i === 0}
              {...props}
            />
          );
        })}
      </div>
    </div>
  );
});

class Booking extends Component {
  static propTypes = {
    match: PropTypes.shape({
      params: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    }),
  };

  constructor(props) {
    super(props);
    const localeData = props.localeData;
    this.state = {
      ...getInitialState(localeData),
    };
    this.getPrice = debounce(this.getPrice, 300);
  }

  componentDidMount() {
    i18next.on('languageChanged', this.onLanguageChanged);
    LocationStore.addChangeListener(this.handleLocationsFetched);
    this.updateStopLoads();
    this.setDefaultShipper();

    const quoteId = this.props.match.params.id;
    if (quoteId && quoteId.length > 10) {
      this.setBookingQuote(quoteId);
    }
  }

  componentWillUnmount() {
    i18next.off('languageChanged', this.onLanguageChanged);
    LocationStore.removeChangeListener(this.handleLocationsFetched);
  }

  onLanguageChanged = () => {
    const { step, errors } = this.state;
    switch (step) {
      case stepNumbers.dispatchDetails:
        if (
          errors.dispatchDetails &&
          Object.keys(errors.dispatchDetails).length > 0
        )
          this.handleDispatchDetailsSubmit();
        break;

      case stepNumbers.loadDetails:
        if (errors.loadDetails && Object.keys(errors.loadDetails).length > 0)
          this.handleLoadDetailsSubmit();
        break;

      case stepNumbers.accessorials:
        if (
          errors.accessorialDetails &&
          Object.keys(errors.accessorialDetails).length > 0
        )
          this.handleAccessorialsSubmit();
        break;

      case stepNumbers.routeDetails:
        if (errors.routeDetails && Object.keys(errors.routeDetails).length > 0)
          this.handleRouteDetailsSubmit();
        break;

      default:
        break;
    }
  };

  async setDefaultShipper() {
    if (SessionStore.isShipper()) {
      const shipper = SessionStore.getUser();
      const stops = await this.getUpdatedStops(shipper, this.state.stops);
      this.setState(
        (prevState) => {
          // Shipper doesn't do this step.
          const completedSteps = { ...prevState.completedSteps };
          completedSteps.dispatchDetails = true;
          return {
            shipper,
            stops,
            hasAccountingHold: shipper.accounting
              ? shipper.accounting.on_hold
              : false,
            completedSteps,
          };
        },
        () => {
          this.getDistance();
          this.getPrice();
          this.fetchLocations(shipper);
        },
      );
    }
  }

  selectShipper = async (_item, shipper) => {
    const stops = await this.getUpdatedStops(shipper, this.state.stops);
    this.setState(
      () => {
        return {
          shipper,
          stops,
          hasAccountingHold: shipper.accounting
            ? shipper.accounting.on_hold
            : false,
        };
      },
      () => {
        this.getDistance();
        this.getPrice();
        this.fetchLocations(shipper);
      },
    );
  };

  fetchLocations = (shipper) => {
    const locations = (shipper.locations || []).map((l) => {
      if (l.location) {
        return l.location._id || l.location;
      }
      return l;
    });
    if (locations.length) {
      CustomersActionCreators.getLocations(locations);
    } else {
      this.setState({ locations: [] });
    }
  };

  getQuote = async (quoteId) => {
    try {
      return await ShipmentWebAPIUtils.fetchQuote(quoteId);
    } catch (e) {
      showToast(
        'Unable to get quote',
        `Unable to get quote: ${e.message || e}`,
        'error',
      );
      return null;
    }
  };

  setBookingQuote = async (quoteId) => {
    const quote = await this.getQuote(quoteId);
    if (!quote) {
      return;
    }
    const currentQuoteId = quote?.quote_id;
    const quoteReferenceNumber = {
      value: currentQuoteId,
      isNew: false,
      editable: false,
      optimized: currentQuoteId,
      slug: 'quote_id',
      name: 'quote_id',
    };

    let accessorialsRequiredDocs = {};

    const isShipper = SessionStore.isShipper();

    // Map quote accessorials to selected state
    const accessorials = this.state.accessorials;
    if (quote.accessorials) {
      Object.keys(quote.accessorials).forEach((key) => {
        const acc = accessorials.find(
          (accessorial) => accessorial.value === key,
        );
        if (acc) {
          if (acc?.required_documents?.length) {
            accessorialsRequiredDocs[key] = true;
          }
          acc.selected = true;
        } else if (isShipper) {
          // Shippers have a restricted list, but if an admin created the quote with
          // an accessorial from the full list, add it for the purpose of this booking.
          const acc = BOOKING_ACCESSORIALS.find(
            (accessorial) => accessorial.value === key,
          );
          if (acc) {
            accessorials.push({ ...acc, selected: true });
          }
        }
      });
    }

    const fakeShipment = {
      origin: quote.origin && quote.origin.length ? quote.origin[0] : {},
      destination:
        quote.destination && quote.destination.length
          ? quote.destination[0]
          : {},
      loads: quote.loads,
    };
    const stops = await this.getUpdatedStops(fakeShipment, this.state.stops);

    this.setState(
      () => ({
        accessorials: accessorials,
        courier: quote.courier,
        distance: quote.distance,
        market: quote.market.code,
        mo_number: '',
        stops,
        loads: quote.loads,
        pallets: quote.pallets || [],
        boxes: quote.boxes || {},
        shipper: quote.shipper,
        weight: quote.weight,
        shipment_type: 'shorthaul',
        completedSteps: {
          dispatchDetails: true,
          loadDetails: false,
          accessorials: false,
          routeDetails: false,
        },
        step: isShipper ? 1 : 2,
        reference_numbers: [quoteReferenceNumber],
        ...accessorialsRequiredDocs,
      }),
      () => {
        this.getDistance();
        this.getPrice();
        this.fetchLocations(quote.shipper);
      },
    );
  };

  getHandleShipmentTypeChange = (shipment_type) => () =>
    this.setState({ shipment_type });

  getShipmentTypeButton = (type) => {
    const { t } = this.props;
    return (
      <Button
        key={type.value}
        active={this.state.shipment_type === type.value}
        onClick={this.getHandleShipmentTypeChange(type.value)}
        id={type.value}
      >
        {titleize(t(sluggify(type.label)))}
      </Button>
    );
  };

  getHandleEquipmentTypeChange = (equipment_types) => {
    const equipment_type = equipment_types.map((eq) => eq.slug);
    this.setState({ equipment_type });
  };

  getActiveTimezone = () => {
    return (
      this.state.selectedTimezone ||
      this.state.originTimezone ||
      DEFAULT_TIMEZONE
    );
  };

  setOriginTimezone = (timezone) => {
    this.setState({ originTimezone: timezone }, () => {
      this.validateUserTimezone(
        this.getActiveTimezone(),
        this.state.userTimezone,
      );
    });
  };

  getHandleTimezoneChange(timezone) {
    const selected = getTimezones().find((tz) => tz.abbr === timezone);
    if (selected) {
      this.setState({ selectedTimezone: selected.value }, () => {
        this.validateUserTimezone(selected.value, this.state.userTimezone);
        this.getPrice();
      });
    }
  }

  onSortEnd = ({ oldIndex, newIndex }) => {
    this.setState(
      (prevState) => {
        const stops = arrayMove(prevState.stops, oldIndex, newIndex).map(
          (stop, i) => ({
            ...stop,
            sequence: i + 1,
          }),
        );
        return {
          stops,
        };
      },
      () => {
        this.getDistance();
      },
    );
  };

  addStop = () => {
    this.setState(
      (prevState) => {
        const newStop = {
          sequence: prevState.stops.length + 1,
          action: 'deliver',
          location: [{ address: '', contact: {} }],
          id: fakeMongoObjectId(),
          appointment_required: false,
        };
        const stops = cloneDeep(prevState.stops);
        stops.push(newStop);
        return {
          stops,
        };
      },
      () => this.updateStopLoads(),
    );
  };

  addLoad = (newLoads) => {
    this.setState({ loads: newLoads }, () => {
      this.getPrice();
      this.updateStopLoads();
    });
  };

  removeStop = (stop) => {
    const stops = cloneDeep(this.state.stops);
    const index = stop.sequence - 1;
    if (index > -1) {
      return stops
        .slice(0, index)
        .concat(stops.slice(index + 1))
        .map((s, i) => {
          return { ...s, sequence: i + 1 };
        });
    }
    return stops;
  };

  replaceStop = (stop) => {
    const stops = cloneDeep(this.state.stops);
    const index = stop.sequence - 1;
    if (index > -1) {
      return stops
        .slice(0, index)
        .concat([stop])
        .concat(stops.slice(index + 1));
    }
    return stops;
  };

  updateStopDetails = async (stop, location, loads) => {
    stop.location[0] = Object.assign(
      { contact: { name: '', phone: '' } },
      location,
    );
    const phone = stop.location[0].contact.phone;
    if (phone.length === 10) {
      stop.location[0].contact.phone = phoneFormatter.format(
        phone,
        '(NNN) NNN-NNNN',
      );
    }
    const market = await MarketsAPIUtils.findMarketV2(stop.location[0].coords);
    if (market) {
      stop.location[0].timezone = market.timezone;
    }
    if (loads) {
      stop.loads = loads;
    }
    /**
     * Why this?
     * When patch the data from Quote
     * appointment_required is inside location, reset the appointment windows
     */
    const requiresAppointment = stop?.location?.[0]?.appointment_required;
    if (requiresAppointment) {
      stop.appointment_required = requiresAppointment;
      stop.window_end = null;
      stop.window_start = null;
    }
    stop.contacts = [stop.location[0].contact];
    return stop;
  };

  getUpdatedStops = async (shipment, stops_in) => {
    const stops = cloneDeep(stops_in);
    if (shipment.origin && shipment.origin.address) {
      let originStop = this.getOriginStop(stops);
      if (originStop) {
        originStop = await this.updateStopDetails(
          originStop,
          shipment.origin,
          shipment.loads,
        );
      }
    }

    if (shipment.destination && shipment.destination.address) {
      let destinationStop = this.getDestinationStop(stops);
      if (destinationStop) {
        destinationStop = await this.updateStopDetails(
          destinationStop,
          shipment.destination,
          shipment.loads,
        );
      }
    }

    return stops;
  };

  handleLocationsFetched = () => {
    const locationResults = LocationStore.getLocations();
    // trim the names
    const mappedLocations = locationResults.map((l) => ({
      ...l,
      name: (l.name || '').trim(),
    }));

    const distinctLocations = uniqBy(mappedLocations, (l) => l.name);
    const locations = sortBy(distinctLocations, (l) => l.name.toLowerCase());
    this.setState({ locations });
  };

  hasCoords = (stop) => {
    return (
      stop && stop.location && stop.location.length && stop.location[0].coords
    );
  };

  // Handle multiple pickup and deliver stops
  async getDistance() {
    const { stops, shipper } = this.state;
    // Verify that all non-empty stops have location coords
    // Can crash the server if there's only 1 stop with a location
    if (
      every(stops, (stop) => {
        return stop.empty || this.hasCoords(stop);
      })
    ) {
      this.setState(
        { distanceFetched: false, nextDisabled: true },
        async () => {
          try {
            const distance = await ShipmentWebAPIUtils.getDistanceV2({
              stops,
              shipperId: shipper?._id,
            });
            this.setState({ distance, distanceFetched: true }, () =>
              this.getPrice(),
            );
          } catch (e) {
            this.setState({ distance: 0 });
            console.log('Error getting distance:', e);
          } finally {
            this.setState({ nextDisabled: false });
          }
        },
      );
    } else {
      this.setState({ distance: 0, nextDisabled: false });
    }
  }

  async getPrice() {
    const shipment = this.createShipment();
    if (shipment.shipper && this.state.distanceFetched) {
      try {
        const price = await ShipmentWebAPIUtils.getPrice(shipment);
        const showFSCDisclaimer = !!price.rerate_fsc;
        this.setState({ price, showFSCDisclaimer });
      } catch (e) {
        this.setState({ price: 0 });
        console.log('Error getting price', e);
      }
    }
  }

  updateReferenceNumber(field, value) {
    this.setState((prevState) => {
      const reference_numbers = [...prevState.reference_numbers];
      const index = reference_numbers.findIndex((ref) => {
        return ref.name === field;
      });
      if (index > -1) {
        if (value) {
          reference_numbers[index].value = value;
          reference_numbers[index].optimized = value.toUpperCase();
        } else {
          reference_numbers.splice(index);
        }
      } else {
        reference_numbers.push({
          name: field,
          value: value,
          optimized: value.toUpperCase(),
        });
      }
      return {
        reference_numbers,
      };
    });
  }

  handleFullTruckloadChange = (value) => {
    this.setState(
      (state) => {
        const full_truckload = value;
        const equipment_type = full_truckload
          ? TRAILER.label
          : state.equipment_type;

        return {
          loads: value ? [] : addDefaultLoad(this.props.localeData),
          full_truckload,
          equipment_type,
        };
      },
      () => this.getPrice(),
    );
  };

  handleSaveReferenceNumbers(reference_numbers) {
    this.setState({ reference_numbers });
    this.hideRefnumModal();
  }

  getHandleHeaderClick = (step) => () => {
    this.setState((prevState) => {
      if (step === prevState.step) {
        return {
          step: null,
        };
      }
      return { step };
    });
  };

  handleShipmentFieldChange(field, value) {
    this.setState({ [field]: value });
    if (REFNUMS.includes(field)) {
      this.updateReferenceNumber(field, value);
    }
  }

  handleRemoveStop(stop) {
    const stops = this.removeStop(stop);
    this.setState({ stops }, () => {
      this.getDistance();
    });
  }

  handleStopFieldChange(stop, field, value) {
    stop[field] = value;
    const stops = this.replaceStop(stop);
    this.setState({ stops });

    if (field === 'reference_numbers') {
      this.setState((prevState) => {
        const reference_numbers = [
          ...prevState.reference_numbers,
          ...stop.reference_numbers,
        ];
        return {
          reference_numbers,
        };
      });
    }
  }

  updateStopLoads() {
    this.setState((prevState) => {
      const stops = cloneDeep(prevState.stops);
      const { loads } = prevState;
      stops.forEach((stop) => {
        // For the simple case of two stops, auto-distribute the shipment loads to them.
        if (stops.length === 2) {
          stop.loads = [...prevState.loads];
        } else {
          if (!stop.loads) {
            stop.loads = [];
          }
          // Leave the quantities alone but update everything else.
          loads.forEach((load, i) => {
            if (stop.loads.length > i) {
              const stopLoad = stop.loads[i];
              stopLoad.type = load.type;
              stopLoad.length = load.length;
              stopLoad.width = load.width;
              stopLoad.height = load.height;
              stopLoad.weight = Math.round(
                (load.weight * stopLoad.quantity) / load.quantity,
              );
              stopLoad.stackable = load.stackable;
            } else {
              // Stop doesn't have this item yet.
              const stopLoad = { ...load };
              stopLoad.quantity = 1;
              stopLoad.weight = Math.round(
                (load.weight * stopLoad.quantity) / load.quantity,
              );
              stop.loads.push(stopLoad);
            }
          });
        }
      });
      return {
        stops,
      };
    });
  }

  removeLoadFromStops(index) {
    this.setState((prevState) => {
      const stops = cloneDeep(prevState.stops);
      stops.forEach((stop) => {
        stop.loads = stop.loads
          .slice(0, index)
          .concat(stop.loads.slice(index + 1));
      });
      return { stops };
    });
  }

  handleLocationFieldChange(stop, field, value) {
    stop.location[0][field] = value;
    const stops = this.replaceStop(stop);
    this.setState({ stops });
  }

  handleLocationNameSelect(stop, selectedLocation) {
    const { name, address, suite_or_apt, lat, lng, coords, contact, market } =
      selectedLocation;

    const timezone = selectedLocation.timezone || market.timezone;
    if (timezone && stop.sequence === 1) {
      this.setOriginTimezone(timezone);
    }

    const location = {
      name: name || '',
      address: address || '',
      suite_or_apt: suite_or_apt || '',
      lat,
      lng,
      coords,
      contact: contact || { name: '', phone: '' },
      timezone,
      market,
    };
    if (location.contact.phone && location.contact.phone.length === 10) {
      location.contact.phone = phoneFormatter.format(
        location.contact.phone,
        '(NNN) NNN-NNNN',
      );
    }

    stop.location[0] = location;
    stop.contacts = [location.contact];
    const stops = this.replaceStop(stop);
    this.setState({ stops }, () => {
      this.getDistance();
    });
  }

  async handleLocationAddressSelect(stop, item) {
    const { location } = item;
    const { address, lat, lng, coords, place_id } = location;
    this.setState({ nextDisabled: true });
    const market = await MarketsAPIUtils.findMarketV2(coords).catch((e) => {
      console.log(e);
      this.setState({ nextDisabled: false });
    });
    if (market) {
      location.timezone = market.timezone;
    }

    if (location.timezone && stop.sequence === 1) {
      this.setOriginTimezone(location.timezone);
    }

    const locationData = Object.assign(
      { ...stop.location[0] },
      {
        address,
        coords,
        lat,
        lng,
        timezone: location.timezone || '',
        market: location.market || market,
        place_id,
      },
    );
    stop.location[0] = locationData;
    const stops = this.replaceStop(stop);
    this.setState({ stops }, () => {
      this.getDistance();
    });
  }

  handleLocationContactChange(stop, attribute, value) {
    const clearedValue = value.replace(/\D/g, '');
    if (attribute === 'phone' && clearedValue.length === 10) {
      value = phoneFormatter.format(clearedValue, '(NNN) NNN-NNNN');
    }
    stop.location[0].contact[attribute] = value;
    stop.contacts = [stop.location[0].contact];
    const stops = this.replaceStop(stop);
    this.setState({ stops });
  }

  handleRemoveLocation = (locationToDelete) =>
    this.setState({ locationToDelete, showConfirmDeleteLocationModal: true });

  handleHideConfirmDeleteLocationModal = () => {
    this.setState({
      locationToDelete: null,
      showConfirmDeleteLocationModal: false,
    });
  };

  handleConfirmDelete = () => {
    const { locationToDelete, shipper } = this.state;
    CustomersActionCreators.removeFavoriteLocation(
      shipper._id,
      locationToDelete._id,
    );
    this.handleHideConfirmDeleteLocationModal();
  };

  handleStartChange(stop, start) {
    stop.window_start = start
      ? this.convertDateToTimezone(start, this.getActiveTimezone())
      : null;
    const stops = this.replaceStop(stop);
    this.setState({ stops });
  }

  handleEndChange(stop, end) {
    stop.window_end = end
      ? this.convertDateToTimezone(end, this.getActiveTimezone())
      : null;
    const stops = this.replaceStop(stop);
    this.setState({ stops });
  }

  updateShipmentWeight() {
    this.setState(
      (prevState) => {
        let { weight } = prevState;
        const { loads } = prevState;
        weight = loads.reduce((total, l) => {
          return total + parseFloat(l.weight) || 0;
        }, 0);
        return {
          weight,
        };
      },
      () => this.getPrice(),
    );
  }

  handleLoadFieldChange = (newLoads, field, value) => {
    this.setState({ loads: newLoads }, () => {
      this.updateStopLoads();
      if (field === 'weight') {
        this.updateShipmentWeight();
      } else if (field === 'weight_units') {
        this.setState({ weight_units: value });
      } else {
        this.getPrice();
      }
    });
  };

  handleRemoveLoad = (newLoads, index) => {
    this.setState({ loads: newLoads }, () => {
      this.updateShipmentWeight();
      this.removeLoadFromStops(index);
    });
  };

  handleAccessorialSelect = (slug) => {
    this.setState(
      (prevState) => {
        const accessorials = [...prevState.accessorials];
        const acc = accessorials.find(
          (accessorial) => accessorial.value === slug,
        );
        if (!acc) {
          return null;
        }
        acc.selected = !acc.selected;

        const updates = {
          accessorials,
        };

        if (acc?.required_documents?.length) {
          updates[acc.value] = acc.selected;
        }

        return updates;
      },
      () => this.getPrice(),
    );
  };

  // Taken from BookingHelper, with the addition of keeping state about what was uploaded.
  handleFileUpload = (key) => (file, type) => {
    // Composite key is later used to extract the document from the store
    this.setState((prevState) => {
      const docKey = `${key}_docs`;
      const docObj = { ...prevState[docKey] };
      docObj[type] = file.name;
      return { [docKey]: docObj };
    });
  };

  handleFileRemove = (key) => (type) => {
    const docKey = `${key}_docs`;
    this.setState(
      (prevState) => {
        const docObj = { ...prevState[docKey] };
        docObj[type] = '';
        return {
          [docKey]: docObj,
        };
      },
      () => {
        DocumentUploadStore.clearDocument(`${key}-${type}`);
      },
    );
  };

  validateDispatchDetails = (state) => {
    const { t } = this.props;
    const dispatchDetails = {};
    if (!state.shipper || !state.shipper._id) {
      dispatchDetails.shipper = t('SELECT_SHIPPER');
    }
    return dispatchDetails;
  };

  validateLoadDetails = (state) => {
    const { t } = this.props;
    const loadErrors = validateLoadDetailsV2({ ...state, t });
    const refnums = {
      tracking_id: state.tracking_id,
      mo_number: state.mo_number,
      house_bill_number: state.house_bill_number,
    };
    const shipperReferenceNumberErrors = validateShipperReferenceNumbers(
      state.shipper,
      refnums,
      t,
    );

    const loadDetails = {
      ...loadErrors,
      ...shipperReferenceNumberErrors,
    };
    return loadDetails;
  };

  validateAccessorialDetails = (state) => {
    const { t } = this.props;
    const accessorialDetails = {};
    this.state.docsRequiredAccessorials.forEach((key) => {
      if (state[key] && !state[`${key}_docs`].do) {
        const name = key?.toUpperCase();
        accessorialDetails[key] = t('UPLOAD_DELIVERY_ORDER_FOR_ACCESSORIAL', {
          name: t(name),
        });
      }
    });
    return accessorialDetails;
  };

  validateDistance = (distance) => {
    const { t, localeData } = this.props;
    const errors = {};
    const formattedDistance = unitConverter(distance).from('m').to('mi');
    const maxDistanceConstant = MAX_DISTANCE_MILES;
    const maxDistanceLimit = maxDistanceConstant;
    if (formattedDistance > maxDistanceLimit) {
      errors.distance = t('MAX_DISTANCE_ERROR', {
        maxDistance: maxDistanceConstant,
        distanceUnit: localeData.distance_units,
        email: SUPPORT_EMAIL_ADDRESS,
      });
    }
    return errors;
  };

  validateLocations = (stops) => {
    const errors = {};
    const { t } = this.props;
    stops.forEach((stop) => {
      const stopLabel = t('STOP_WITH_SEQUENCE', { sequence: stop.sequence });
      const location = stop.location[0];
      if (!location.name) {
        errors[`name-${stop.sequence}`] = t('PROVIDE_LOCATION_NAME', {
          stopLabel,
        });
      }
      if (!location.address) {
        errors[`address-${stop.sequence}`] = t('PROVIDE_LOCATION_ADDRESS', {
          stopLabel,
        });
      }
      const phone = location.contact ? location.contact.phone : null;
      if (phone) {
        if (phone.length < 10) {
          errors[`phone-${stop.sequence}`] = t('PHONE_NUMBER_WITH_AREA_CODE', {
            stopLabel,
          });
        } else if (phone.includes('(') && phone.length !== 14) {
          errors[`phone-${stop.sequence}`] = t('PHONE_NUMBER_WITH_AREA_CODE', {
            stopLabel,
          });
        }
      }
      if (location.address && !location.market) {
        errors[`market-${stop.sequence}`] = t(
          'LOCATION_NOT_COVERED_BY_MARKET',
          { stopLabel },
        );
      }
    });
    return errors;
  };

  validateCFSScheduling(stops) {
    const { t } = this.props;
    const destination = this.getDestinationStop(stops);
    const timezone = this.getActiveTimezone();
    const deliver_after = destination.window_start;
    const timeWindow = {};
    let cutoffTime;
    let deliverTime;
    let offset;

    if (timezone) {
      const now = moment().tz(timezone);
      offset = now.format('Z');
      const cutoffDateStr = `${now.format('YYYY-MM-DD')} 14:00:00${offset}`;
      cutoffTime = moment.tz(cutoffDateStr, timezone);
      deliverTime = moment.tz(deliver_after, timezone);
    } else {
      const now = moment();
      offset = now.format('Z');
      const cutoffDateStr = `${now.format('YYYY-MM-DD')} 14:00:00${offset}`;
      cutoffTime = moment(cutoffDateStr);
      deliverTime = moment(deliver_after);
    }

    const isSameDayDelivery = deliverTime.isSameOrBefore(cutoffTime, 'day');
    if (isSameDayDelivery && deliverTime.isSameOrAfter(cutoffTime)) {
      timeWindow.pickup_after = t('DELIVERY_WINDOW_PRIOR_TO_TWO_PM');
    }

    return timeWindow;
  }

  validateShipperScheduling(stops) {
    const { distance } = this.state;
    const { t, localeData } = this.props;
    const origin = this.getOriginStop(stops);
    const destination = this.getDestinationStop(stops);
    const timezone = this.getActiveTimezone();

    const deliverBy = moment.tz(destination.window_end, timezone);
    const pickupBy = moment.tz(origin.window_end, timezone);
    const isSameDayShipment = deliverBy.isSame(pickupBy, 'day');

    const timeWindow = {};
    const formattedDistance = unitConverter(distance).from('m').to('mi');
    const maxDistanceConstant = MAX_DISTANCE_MILES_SAME_DAY;
    const maxDistanceLimit = maxDistanceConstant;
    if (isSameDayShipment && formattedDistance > maxDistanceLimit) {
      timeWindow.distance = t('MAX_DISTANCE_ERROR_FOR_SAME_DAY_SHIPMENTS', {
        maxDistanceSameDay: maxDistanceConstant,
        distanceUnit: localeData.distance_units,
      });
    }

    if (
      pickupBy.diff(moment.tz(origin.window_start, timezone), 'hours') <
      MIN_WINDOW_SIZE
    ) {
      timeWindow.pickup_by = t('PICKUP_HOUR_ERROR', {
        minWindowSize: MIN_WINDOW_SIZE,
      });
    } else if (
      moment(destination.window_end).diff(
        moment(destination.window_start),
        'hours',
      ) < MIN_WINDOW_SIZE
    ) {
      timeWindow.deliver_by = t('DELIVERY_HOUR_ERROR', {
        minWindowSize: MIN_WINDOW_SIZE,
      });
    } else if (deliverBy.diff(pickupBy, 'hours') < MIN_WINDOW_SIZE) {
      timeWindow.deliver_by = t('PICKUP_AND_DELIVERY_WINDOW_ERROR', {
        minWindowSize: MIN_WINDOW_SIZE,
      });
    }

    return timeWindow;
  }

  validateSchedule = (stops) => {
    const { t } = this.props;
    let CFSerrors = {};
    if (this.state.cfs) {
      CFSerrors = this.validateCFSScheduling(stops);
    }
    let shipperSchedulingErrors = {};
    if (SessionStore.isShipper()) {
      shipperSchedulingErrors = this.validateShipperScheduling(stops);
    }
    const scheduleErrors = {
      ...CFSerrors,
      ...shipperSchedulingErrors,
    };
    stops.forEach((stop) => {
      const stopLabel = t('STOP_WITH_SEQUENCE', { sequence: stop.sequence });
      if (
        !stop?.appointment_required &&
        (!stop.window_start || !stop.window_end)
      ) {
        scheduleErrors[`appt-${stop.sequence}`] = t('SET_AN_APPOINTMENT', {
          stopLabel,
        });
      }
    });
    return scheduleErrors;
  };

  getStopActionLoadQuantity = (stops, action, loadIndex) => {
    // For a given shipment load index, return the total quantity allocated to stops with the specified action.
    return stops.reduce((total, stop) => {
      let stopLoad = null;
      if (
        stop.action === action &&
        stop.loads &&
        stop.loads.length > loadIndex
      ) {
        stopLoad = stop.loads[loadIndex];
      }
      return total + (stopLoad ? stopLoad.quantity : 0);
    }, 0);
  };

  validateStopLoads = (stops, loads) => {
    const { t } = this.props;
    const errors = {};
    stops.forEach((stop) => {
      const stopLabel = t('STOP_WITH_SEQUENCE', { sequence: stop.sequence });
      if (!stop.loads || !stop.loads.length) {
        errors[`loads-${stop.sequence}`] = t('ASSIGN_LOAD', { stopLabel });
      }
    });
    loads.forEach((load, loadIndex) => {
      const pickupQty = this.getStopActionLoadQuantity(
        stops,
        'pickup',
        loadIndex,
      );
      const deliverQty = this.getStopActionLoadQuantity(
        stops,
        'deliver',
        loadIndex,
      );
      if (pickupQty !== load.quantity) {
        errors[`all-loads-${loadIndex}-pickup`] = t(
          'LOAD_QUANTITY_PICKUP_MISMATCH_ERROR',
          { loadIndex: loadIndex + 1, loadQuantity: load.quantity, pickupQty },
        );
      }
      if (deliverQty !== load.quantity) {
        errors[`all-loads-${loadIndex}-deliver`] = t(
          'LOAD_QUANTITY_DELIVERY_MISMATCH_ERROR',
          { loadIndex: loadIndex + 1, loadQuantity: load.quantity, deliverQty },
        );
      }
    });
    return errors;
  };

  validateUserTimezone = (activeTimezone, userTimezone) => {
    const userTimezoneErrors = {};
    const { t } = this.props;
    if (activeTimezone && activeTimezone !== userTimezone) {
      const userTZ = getAbbreviatedTimezone(userTimezone);
      const activeTZ = getAbbreviatedTimezone(userTimezone);
      userTimezoneErrors.timezone = t('USER_TIMEZONE_WARNING', {
        userTZ,
        activeTZ,
      });
    }
    this.setState((state) => ({
      errors: { ...state.errors, userTimezoneErrors },
    }));
  };

  validateRouteDetails = (state) => {
    const { stops, loads, full_truckload, distance } = state;
    const locationErrors = this.validateLocations(stops);

    let distanceErrors = {};
    if (SessionStore.isShipper()) {
      distanceErrors = this.validateDistance(distance);
    }
    const scheduleErrors = this.validateSchedule(stops);
    const loadErrors = !full_truckload
      ? this.validateStopLoads(stops, loads)
      : {};

    return {
      ...locationErrors,
      ...distanceErrors,
      ...scheduleErrors,
      ...loadErrors,
    };
  };

  previousStep = () => {
    this.setState((state) => ({ step: state.step - 1 }));
  };

  nextStep = () => {
    this.setState((state) => ({ step: state.step + 1 }));
  };

  handleDispatchDetailsSubmit = () => {
    this.setState(
      (state) => {
        const completedSteps = cloneDeep(state.completedSteps);
        const dispatchDetails = this.validateDispatchDetails(state);
        if (isValid(dispatchDetails)) {
          completedSteps.dispatchDetails = true;
        } else {
          completedSteps.dispatchDetails = false;
        }
        return { completedSteps, errors: { ...state.errors, dispatchDetails } };
      },
      () => {
        if (this.state.completedSteps.dispatchDetails) {
          this.nextStep();
        }
      },
    );
  };

  handleLoadDetailsSubmit = () => {
    this.setState(
      (state) => {
        const completedSteps = cloneDeep(state.completedSteps);
        const loadDetails = this.validateLoadDetails(state);
        if (isValid(loadDetails)) {
          completedSteps.loadDetails = true;
        } else {
          completedSteps.loadDetails = false;
        }
        return { completedSteps, errors: { ...state.errors, loadDetails } };
      },
      () => {
        if (this.state.completedSteps.loadDetails) {
          this.nextStep();
        }
      },
    );
  };

  handleAccessorialsSubmit = () => {
    this.setState(
      (state) => {
        const completedSteps = cloneDeep(state.completedSteps);
        const accessorialDetails = this.validateAccessorialDetails(state);
        if (isValid(accessorialDetails)) {
          completedSteps.accessorials = true;
        } else {
          completedSteps.accessorials = false;
        }

        return {
          completedSteps,
          errors: { ...state.errors, accessorialDetails },
        };
      },
      () => {
        if (this.state.completedSteps.accessorials) {
          this.nextStep();
        }
      },
    );
  };

  handleRouteDetailsSubmit = () => {
    this.setState(
      (state) => {
        const completedSteps = cloneDeep(state.completedSteps);
        const routeDetails = this.validateRouteDetails(state);
        if (isValid(routeDetails)) {
          completedSteps.routeDetails = true;
        } else {
          completedSteps.routeDetails = false;
        }
        return { completedSteps, errors: { ...state.errors, routeDetails } };
      },
      () => {
        if (this.state.completedSteps.routeDetails) {
          this.nextStep();
        }
      },
    );
  };

  // Disable the booking button until certain fields are input
  valid() {
    const { stops, shipper, distance, loads, full_truckload, weight } =
      this.state;
    const loadQuantity = loads.reduce((total, load) => {
      return total + load.quantity;
    }, 0);
    const loadExists = loadQuantity || full_truckload;
    const allStopsHaveAddress = every(stops, (s) => {
      return !isEmpty(s.location[0].address);
    });

    return (
      shipper &&
      !isEmpty(shipper._id) &&
      weight > 0 &&
      distance > 0 &&
      loadExists &&
      allStopsHaveAddress
    );
  }

  convertDateToTimezone(date, timezone) {
    return moment(date).seconds(0).tz(timezone, true).format();
  }

  getOriginStop(stops) {
    const origin = stops.find((stop) => {
      return stop.action === 'pickup' && !stop.empty;
    });
    return origin || stops[0];
  }

  getDestinationStop(stops) {
    const destination = stops
      .filter((stop) => {
        return stop.action === 'deliver' && !stop.empty;
      })
      .pop();
    return destination || stops[stops.length - 1];
  }

  createShipment() {
    const {
      stops,
      loads,
      weight,
      weight_units,
      full_truckload,
      shipment_type,
      notes,
      reference_numbers,
      distance,
      project,
      hold_at_dispatch,
      exception_exempt,
      business,
      accessorials,
    } = this.state;
    const { t } = this.props;
    // boxes and pallets are needed for pricing.
    // The job schema only supports a single object with box dimensions.
    // I guess we could aggregate multiple box items with the same dimension... later.
    const boxItem = loads.find((l) => l.type === 'boxes');
    const boxes =
      !full_truckload && boxItem
        ? pick(boxItem, 'quantity', 'length', 'width', 'height')
        : {};
    const pallets = !full_truckload
      ? loads.filter((l) => l.type === 'pallets')
      : [];
    const shipment = Object.assign(
      {},
      {
        shipment_type,
        shipper: (this.state.shipper || {}).email,
        stops,
        loads,
        boxes,
        pallets,
        full_truckload,
        notes,
        reference_numbers,
        distance,
        weight,
        weight_units,
        project,
        hold_at_dispatch,
        exception_exempt,
        business,
        accessorials: [],
        documents: [],
        payables: [],
        receivables: [],
        courier: false,
      },
    );
    if (stops[0].location[0].market) {
      shipment.market = stops[0].location[0].market;
    }
    if (this.state.equipment_type) {
      shipment.equipment_type = this.state.equipment_type;
    }

    accessorials
      .filter((acc) => acc.selected)
      .forEach((acc) => {
        const accessorial = {
          category: 'accessorials',
          type: acc.value,
          message: acc.description,
          timestamp: new Date(),
        };
        shipment.receivables.push(
          Object.assign({}, accessorial, { value: acc.rate_to_shipper }),
        );
        shipment.payables.push(
          Object.assign({}, accessorial, { value: acc.rate_to_carrier }),
        );

        if (this.state.docsRequiredAccessorials.includes(acc.value)) {
          acc?.required_documents?.forEach((type) => {
            const doc = DocumentUploadStore.getDocument(`${acc.value}-${type}`);
            if (doc) {
              const key = type === 'do' ? 'delivery_order' : 'form_atme';
              const name = type === 'do' ? t('DELIVERY_ORDER') : t('FORM_ATME');
              // Server will update path if needed.
              shipment.documents.push({
                key,
                name,
                path: doc.uri,
                uri: doc.uri,
                uri_type: doc.uri_type,
              });
            } else if (type === 'do') {
              // This is transient while on the Accessorials step. Can't continue until DO is uploaded.
              console.log(`Missing delivery order for ${acc.description}`);
            }
          });
        }
        // Needed by pricing service.
        shipment.accessorials.push(accessorial);
      });
    /**
     * Why This?
     * If shipment has any stop with appointment_required
     * Add appointment accessorial only in receivables with 0 cost
     */
    const isAppointmentRequired = stops.some(
      (stop) => stop.appointment_required,
    );
    const isAppointmentAccessorialPresent = shipment?.receivables?.some(
      (accessorial) => accessorial.type === 'appointment',
    );
    if (isAppointmentRequired && !isAppointmentAccessorialPresent) {
      shipment.receivables.push({
        category: 'accessorials',
        type: 'appointment',
        message: 'Appointment',
        timestamp: new Date(),
        value: 0,
      });
    }
    if (this.state.doName) {
      const deliveryOrder = DocumentUploadStore.getDocument('delivery_order');
      if (deliveryOrder && deliveryOrder.uri) {
        shipment.delivery_order = deliveryOrder.uri;
      }
    }

    const timezone = this.getActiveTimezone();
    const origin = this.getOriginStop(stops);
    const destination = this.getDestinationStop(stops);
    if (timezone) {
      shipment.pickup_after = moment
        .utc(this.convertDateToTimezone(origin.window_start, timezone))
        .format();
      shipment.pickup_by = moment
        .utc(this.convertDateToTimezone(origin.window_end, timezone))
        .format();
      shipment.deliver_after = moment
        .utc(this.convertDateToTimezone(destination.window_start, timezone))
        .format();
      shipment.deliver_by = moment
        .utc(this.convertDateToTimezone(destination.window_end, timezone))
        .format();
    }

    return shipment;
  }

  submitBooking = async () => {
    this.setState({ booking_error: null, booking: true }, async () => {
      try {
        const shipment = this.createShipment();
        delete shipment.accessorials; // already in payables & receivables
        const data = { shipment, action: 'add' };
        const offer = await ShipmentWebAPIUtils.bookV2(data);

        if (this.state.deliveryOrderDocument) {
          await attachDocuments(
            [this.state.deliveryOrderDocument.id],
            [offer._id],
          );
        }

        if (
          this.state.accessorialDocumentsAirportPickup.length &&
          this.state.airport_pickup
        ) {
          const accessorialDocumentAirportPickupIDs =
            this.state.accessorialDocumentsAirportPickup.map(
              (accessorialDocument) => accessorialDocument.id,
            );
          await attachDocuments(accessorialDocumentAirportPickupIDs, [
            offer._id,
          ]);
        }

        if (
          this.state.airport_delivery &&
          this.state.accessorialDocumentsAirportDelivery?.length
        ) {
          const accessorialDcoumentAirportDeliveryIDs =
            this.state.accessorialDocumentsAirportDelivery?.map(
              (accessorialDocument) => accessorialDocument.id,
            );
          await attachDocuments(accessorialDcoumentAirportDeliveryIDs, [
            offer._id,
          ]);
        }

        if (
          this.state.cfs_pickup &&
          this.state.accessorialDocumentsCfsPickup?.length
        ) {
          const accessorialDocumentCfsPickupIDs =
            this.state.accessorialDocumentsCfsPickup?.map(
              (accessorialDocument) => accessorialDocument.id,
            );
          await attachDocuments(accessorialDocumentCfsPickupIDs, [offer._id]);
        }

        if (
          this.state.cfs_delivery &&
          this.state.accessorialDocumentsCfsDelivery?.length
        ) {
          const accessorialDocumentCfsDeliveryIDs =
            this.state.accessorialDocumentsCfsDelivery?.map(
              (accessorialDocument) => accessorialDocument.id,
            );
          await attachDocuments(accessorialDocumentCfsDeliveryIDs, [offer._id]);
        }

        const offerSubset = { _id: offer._id, shipment_id: offer.shipment_id };
        const localeData = this.props.localeData;
        this.setState({
          ...getInitialState(localeData),
          booking_error: null,
          offer: offerSubset,
        });
        this.setDefaultShipper();
      } catch (e) {
        this.setState({
          booking: false,
          offer: null,
          booking_error: e.message,
        });
      }
    });
  };

  showRefnumModal = () => {
    this.setState({ showAddRefnumsModal: true });
  };

  hideRefnumModal = () => {
    this.setState({ showAddRefnumsModal: false });
  };

  renderDispatchDetailErrors() {
    const {
      errors: { dispatchDetails },
    } = this.state;
    if (!dispatchDetails) {
      return null;
    }
    return renderErrors(dispatchDetails);
  }

  renderLoadDetailErrors() {
    const { t } = this.props;
    const {
      errors: { loadDetails },
    } = this.state;
    if (!loadDetails) {
      return null;
    }
    const nonLoadItemErrors = omit(loadDetails, 'loadItemErrors');

    return (
      <div className="mb-4">
        {nonLoadItemErrors && renderErrors(nonLoadItemErrors)}

        {loadDetails.loadItemErrors.map((errors, i) => {
          return (
            <div key={i}>
              {renderErrors(errors, t('LOAD_ERROR', { loadCount: i + 1 }))}
            </div>
          );
        })}
      </div>
    );
  }

  renderAccessorialDetailErrors() {
    const {
      errors: { accessorialDetails },
    } = this.state;
    if (!accessorialDetails) {
      return null;
    }
    return renderErrors(accessorialDetails);
  }

  renderRouteDetailErrors() {
    const {
      errors: { routeDetails },
    } = this.state;
    if (!routeDetails) {
      return null;
    }
    return renderErrors(routeDetails);
  }

  renderUserTimezoneErrors() {
    const {
      errors: { userTimezoneErrors },
    } = this.state;
    if (!userTimezoneErrors) {
      return null;
    }
    return renderWarnings(userTimezoneErrors, 'TIMEZONE_WARNING');
  }

  renderTimezoneSelector() {
    return (
      <TimezoneSelect
        selectedTimezone={getAbbreviatedTimezone(this.getActiveTimezone())}
        onTimezoneChange={(timezone) => this.getHandleTimezoneChange(timezone)}
        timezones={getTimezones()}
      />
    );
  }

  renderConfirmDeleteLocationModal() {
    const { t } = this.props;
    if (!this.state.showConfirmDeleteLocationModal) {
      return null;
    }
    const {
      locationToDelete: { name },
    } = this.state;
    const message = t('LOCATION_DELETE_CONFIRMATION_MSG', { name: name });
    return (
      <ConfirmModal
        show={this.state.showConfirmDeleteLocationModal}
        title={t('DELETE_CONFIRMATION')}
        message={message}
        onConfirm={this.handleConfirmDelete}
        onHide={this.handleHideConfirmDeleteLocationModal}
        hideButtonText={t('CANCEL')}
        affirmative={t('OK')}
      />
    );
  }

  renderAccountingHoldModal() {
    const { t } = this.props;
    return (
      <Modal
        show={this.state.showAccountingHoldModal}
        onHide={() => this.setState({ showAccountingHoldModal: false })}
        center
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('RESTRICTED')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('ACCOUNT_BOOKING_RESTRICTED')}</Modal.Body>
      </Modal>
    );
  }

  renderAddRefnumsModal() {
    if (!this.state.showAddRefnumsModal) {
      return null;
    }
    return (
      <EditReferenceNumbersModal
        show={this.state.showAddRefnumsModal}
        reference_numbers={this.state.reference_numbers}
        onClose={this.hideRefnumModal.bind(this)}
        onSave={this.handleSaveReferenceNumbers.bind(this)}
        page="Booking"
      />
    );
  }

  renderShipmentTypes() {
    return SHIPMENT_TYPES.map(this.getShipmentTypeButton);
  }

  renderEquipmentTypes() {
    const { t } = this.props;

    const selectedShipmentTypes = Array.isArray(this.state.shipment_type)
      ? this.state.shipment_type
      : [this.state.shipment_type];

    const equipment_types = getEquipmentTypesForShipmentTypes(
      selectedShipmentTypes,
    ).map((type) => {
      return {
        name: t(type.label),
        slug: type.label,
        selected: this.state.equipment_type.indexOf(type.label) > -1,
      };
    });

    return (
      <div className="max-w-sm pb-2">
        <Multiselect
          label={t('SELECT_EQUIPMENT_TYPE')}
          items={equipment_types}
          onSelect={this.getHandleEquipmentTypeChange}
        />
      </div>
    );
  }

  renderStepButtons(nextFunc = {}, showBack = true) {
    const { t } = this.props;
    const { nextDisabled } = this.state;
    return (
      <div className="form-actions space-x-4">
        {showBack && (
          <button className="button button-gray" onClick={this.previousStep}>
            <div className="flex items-center space-x-1">
              <CircleArrowLeftIcon />
              <span className="block">{titleize(t('BACK'))}</span>
            </div>
          </button>
        )}
        <button
          className="button button-green"
          onClick={nextFunc}
          disabled={this.state.hasAccountingHold || nextDisabled}
        >
          {nextDisabled ? (
            <Spinner showMessage={t('BOOKING_NEXT_DISABLED_MESSAGE')} />
          ) : (
            <div className="flex items-center space-x-2">
              <span className="block my-0">{titleize(t('NEXT'))}</span>
              <CircleArrowRightIcon />
            </div>
          )}
        </button>
      </div>
    );
  }

  renderDispatch() {
    const { t } = this.props;
    return (
      <div>
        <AccordionSection
          title={t('DISPATCH_DETAILS')}
          stepNumber={stepNumbers.dispatchDetails}
          onHeaderClick={this.getHandleHeaderClick(stepNumbers.dispatchDetails)}
          disabled={false}
          isActive={this.state.step === stepNumbers.dispatchDetails}
        >
          <div className="grid lg:grid-cols-4 lg:gap-4">
            <div className="lg:col-span-2">
              <label className="mb-1 form-label">
                {titleize(t('SHIPPER', { count: 1 }))}
              </label>
              <CustomerSearch
                clearOnSelect
                onSelect={this.selectShipper}
                type="booking_shippers"
                dropdownStyle={{ maxHeight: '160px', overflow: 'auto' }}
                menuStyle={{
                  position: 'absolute',
                  top: 40,
                  left: 0,
                }}
              />
              {this.state.shipper && (
                <div className="my-4">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-500">
                      {t('SELECTED_SHIPPER')}:
                    </span>
                    <span className="ml-2 text-sm text-gray-900">
                      {unescapeHTML(this.state.shipper.company_name)}
                    </span>
                    {this.state.hasAccountingHold && (
                      <CargomaticBadge color="red" className="ml-1">
                        <div className="flex items-center">
                          <span>{t('RESTRICTED')}</span>
                          <CargomaticTooltip className="ml-1">
                            {t('BOOKING_RESTRICTED_FOR_ACCOUNT')}
                          </CargomaticTooltip>
                        </div>
                      </CargomaticBadge>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="mb-4 lg:col-span-2 space-y-6">
              <div>
                <label className="mb-1 form-label">{t('SHIPMENT_TYPE')}</label>
                <div className="">
                  <ButtonGroup className="margined-right shipment-btn">
                    {this.renderShipmentTypes()}
                  </ButtonGroup>
                </div>
              </div>
              <div>
                <label className="mb-1 form-label">
                  {t('DISPATCH_OPTIONS')}
                </label>
                <div className="">
                  <div className="pr-8">
                    <CargomaticInput
                      id="project"
                      type="checkbox"
                      label={t('PROJECT')}
                      info={null}
                      value={this.state.project}
                      onChange={(value) => this.setState({ project: value })}
                    />
                  </div>
                  <div className="pr-8">
                    <CargomaticInput
                      id="hold_at_dispatch"
                      type="checkbox"
                      label={t('EXPOSE_MANUALLY_LABEL')}
                      info={t('EXPOSE_MANUALLY_INFO')}
                      value={this.state.hold_at_dispatch}
                      onChange={(value) => {
                        this.setState({ hold_at_dispatch: value });
                      }}
                    />
                  </div>
                  <div className="pr-8">
                    <CargomaticInput
                      id="exception_exempt"
                      type="checkbox"
                      label={t('EXEMPT_FROM_EXCEPTIONS')}
                      info={null}
                      value={this.state.exception_exempt}
                      onChange={(value) => {
                        this.setState({ exception_exempt: value });
                      }}
                    />
                  </div>
                  <div className="pr-8">
                    <CargomaticInput
                      id="b2b"
                      type="checkbox"
                      label={t('B2B')}
                      info={null}
                      value={this.state.business}
                      onChange={(value) => this.setState({ business: value })}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {this.renderDispatchDetailErrors()}
          {this.renderStepButtons(this.handleDispatchDetailsSubmit, false)}
        </AccordionSection>
      </div>
    );
  }

  renderStopList() {
    const { stops, loads, shipment_type } = this.state;

    return (
      <SortableStopsList
        stops={stops}
        loads={loads}
        shipment_type={shipment_type}
        timezone={this.getActiveTimezone()}
        onSortEnd={this.onSortEnd}
        useDragHandle
        lockAxis="y"
        locations={this.state.locations}
        onLocationContactChange={this.handleLocationContactChange.bind(this)}
        onStopFieldChange={this.handleStopFieldChange.bind(this)}
        onLocationFieldChange={this.handleLocationFieldChange.bind(this)}
        onNameSelect={this.handleLocationNameSelect.bind(this)}
        onAddressSelect={this.handleLocationAddressSelect.bind(this)}
        onRemoveLocation={this.handleRemoveLocation}
        onRemoveStop={this.handleRemoveStop.bind(this)}
        onStartChange={this.handleStartChange.bind(this)}
        onEndChange={this.handleEndChange.bind(this)}
      />
    );
  }

  renderShipmentNotes() {
    const { t } = this.props;
    return (
      <div className="mb-4">
        <CargomaticInput
          type="textarea"
          rows={4}
          label={t('SHIPMENT_NOTE_other', { count: 2 })}
          placeholder={t('SHIPMENT_NOTE_other', { count: 2 })}
          value={this.state.notes}
          onChange={(notes) => this.setState({ notes })}
        />
      </div>
    );
  }

  renderRequiredAppointmentNote() {
    const { t } = this.props;
    const isAppointmentRequired = this.state?.stops?.some(
      (stop) => stop.appointment_required,
    );
    if (!isAppointmentRequired) return null;
    return (
      <CargomaticAlert
        dataTestId="Alert-RequiredAppointment-Booking"
        className="flex-1 mb-4"
        type="warning"
        title={t('REQUIRED_APPOINTMENT_NOTES')}
      ></CargomaticAlert>
    );
  }

  renderRoute() {
    const { t } = this.props;
    const tooltip = <Tooltip id="add-stop-tooltip">{t('ADD_STOP')}</Tooltip>;
    return (
      <div className="mt-4">
        <AccordionSection
          title={t('ROUTE_DETAILS')}
          stepNumber={stepNumbers.routeDetails}
          onHeaderClick={this.getHandleHeaderClick(stepNumbers.routeDetails)}
          disabled={!this.state.completedSteps.accessorials}
          isActive={this.state.step === stepNumbers.routeDetails}
        >
          {this.renderUserTimezoneErrors()}
          <div className="flex justify-between">
            <header className="flex items-center mb-4 space-x-2">
              <h3 className="my-0">{t('STOP', { count: 2 })}</h3>

              {SessionStore.hasMultiStopBookingRole() && (
                <OverlayTrigger placement="top" overlay={tooltip}>
                  <AddButton title="Add Stop" onClick={this.addStop} />
                </OverlayTrigger>
              )}
            </header>
            {this.renderTimezoneSelector()}
          </div>
          {this.renderStopList()}
          {this.renderShipmentNotes()}
          {this.renderRequiredAppointmentNote()}
          {this.renderRouteDetailErrors()}
          {this.renderStepButtons(this.handleRouteDetailsSubmit)}
        </AccordionSection>
      </div>
    );
  }

  renderIds() {
    const { t } = this.props;
    return (
      <div>
        <h3 className="my-0 mb-4">{titleize(t('REFERENCE_NUMBERS'))}</h3>
        <div className="grid lg:grid-cols-3 lg:gap-x-4 lg:gap-y-4">
          <div className="">
            <CargomaticInput
              label={t('TRACKING_ID')}
              placeholder={t('TRACKING_ID')}
              info={t('YOUR_SHIPMENT_ID')}
              value={this.state.tracking_id}
              onChange={(value) => {
                this.handleShipmentFieldChange('tracking_id', value);
              }}
            />
          </div>
          <div className="">
            <CargomaticInput
              label={t('MO_NUMBER')}
              placeholder={t('MO_NUMBER')}
              value={this.state.mo_number}
              onChange={(value) => {
                this.handleShipmentFieldChange('mo_number', value);
              }}
            />
          </div>
          <div className="">
            <CargomaticInput
              label={t('HOUSE_BILL_NUMBER')}
              placeholder={t('HOUSE_BILL_NUMBER')}
              value={this.state.house_bill_number}
              onChange={(value) => {
                this.handleShipmentFieldChange('house_bill_number', value);
              }}
            />
          </div>
        </div>
        <button
          className="mt-4 button button-blue"
          onClick={this.showRefnumModal}
        >
          {t('ADD_REFERENCE_NUMBERS')}
        </button>
      </div>
    );
  }

  renderLoadHeader() {
    const { t } = this.props;
    return (
      <div className="lg:grid lg:gap-x-4 lg:gap-y-6 lg:grid-cols-3">
        <div className="mb-2 lg:col-span-2 space-y-4">
          <AuthorizationWrapper excludedUserTypes={[USER_TYPES.SHIPPER]}>
            <div>
              <label className="mb-1 form-label">{t('EQUIPMENT')}</label>
              <ButtonGroup className="margined-right shipment-btn">
                {this.renderEquipmentTypes()}
              </ButtonGroup>
            </div>
          </AuthorizationWrapper>

          <CargomaticInput
            className="my-4"
            type="checkbox"
            id="full_truckload"
            label={t('FULL_TRUCKLOAD')}
            info={t('FULL_TRUCKLOAD_NOTE')}
            value={this.state.full_truckload}
            onChange={this.handleFullTruckloadChange}
          />
        </div>
      </div>
    );
  }

  onTotalShipmentWeightChange = (value) => {
    console.log('Total shipment weight change', value);
    this.setState({
      weight: !Number.isNaN(value) ? parseFloat(value) : undefined,
    });
  };

  renderLoads() {
    const showBackButton = SessionStore.isAdmin();
    const { t } = this.props;
    return (
      <div className="mt-4">
        <AccordionSection
          title={t('LOAD_DETAILS')}
          stepNumber={stepNumbers.loadDetails}
          onHeaderClick={this.getHandleHeaderClick(stepNumbers.loadDetails)}
          disabled={!this.state.completedSteps.dispatchDetails}
          isActive={this.state.step === stepNumbers.loadDetails}
        >
          {this.renderLoadHeader()}
          <hr />
          <LoadDetailsForm
            loads={this.state.loads}
            fullTruckload={this.state.full_truckload}
            totalShipmentWeight={this.state.weight}
            shipmentWeightUnit={this.state.weight_units}
            addLoad={this.addLoad}
            onRemoveLoad={this.handleRemoveLoad}
            onLoadFieldChange={this.handleLoadFieldChange}
            onTotalShipmentWeightChange={this.onTotalShipmentWeightChange}
          />
          <hr />
          {this.renderIds()}
          <hr />
          <BookingDeliveryOrder
            onUpload={(deliveryOrderDocument) => {
              this.setState({
                deliveryOrderDocument,
              });
            }}
            onDelete={() => {
              this.setState({
                deliveryOrderDocument: null,
              });
            }}
          />
          <hr />
          {this.renderLoadDetailErrors()}
          {this.renderStepButtons(this.handleLoadDetailsSubmit, showBackButton)}
        </AccordionSection>
      </div>
    );
  }

  renderAccessorials() {
    const { t } = this.props;
    const { airport_pickup, airport_delivery, cfs_pickup, cfs_delivery } =
      this.state;
    const docsRequired =
      airport_delivery || airport_pickup || cfs_delivery || cfs_pickup;
    return (
      <div className="mt-4">
        <AccordionSection
          title={t('ACCESSORIAL', { count: 2 })}
          stepNumber={stepNumbers.accessorials}
          onHeaderClick={this.getHandleHeaderClick(stepNumbers.accessorials)}
          disabled={!this.state.completedSteps.loadDetails}
          isActive={this.state.step === stepNumbers.accessorials}
        >
          <AccessorialGrid
            accessorials={this.state.accessorials}
            handleSelect={this.handleAccessorialSelect.bind(this)}
          />
          {docsRequired && [
            <hr key="accessorial-docs-separator" />,
            <h3 key="accessorial-docs-header" className="my-0 mb-2">
              {t('ACCESSORIAL_DOCUMENTS')}
            </h3>,
          ]}
          <div className="flex flex-row space-x-4">
            {this.state.airport_pickup && (
              <div className="mb-4">
                <AccessorialUpload
                  onFileUpload={this.handleFileUpload('airport_pickup')}
                  onFileRemove={this.handleFileRemove('airport_pickup')}
                  title={
                    BOOKING_ACCESSORIALS_MAP.airport_pickup.localizeDescription
                  }
                  docs={this.state.airport_pickup_docs}
                  onUpload={(accessorialDocument) => {
                    this.setState((prevState) => {
                      return {
                        accessorialDocumentsAirportPickup: [
                          ...prevState.accessorialDocumentsAirportPickup,
                          accessorialDocument,
                        ],
                      };
                    });
                  }}
                />
              </div>
            )}
            {this.state.airport_delivery && (
              <div className="mb-4">
                <AccessorialUpload
                  onFileUpload={this.handleFileUpload('airport_delivery')}
                  onFileRemove={this.handleFileRemove('airport_delivery')}
                  title={
                    BOOKING_ACCESSORIALS_MAP.airport_delivery
                      .localizeDescription
                  }
                  docs={this.state.airport_delivery_docs}
                  onUpload={(accessorialDocument) => {
                    this.setState((prevState) => {
                      return {
                        accessorialDocumentsAirportDelivery: [
                          ...prevState.accessorialDocumentsAirportDelivery,
                          accessorialDocument,
                        ],
                      };
                    });
                  }}
                />
              </div>
            )}
            {this.state.cfs_pickup && (
              <div className="mb-4">
                <AccessorialUpload
                  atmeRequired={false}
                  onFileUpload={this.handleFileUpload('cfs_pickup')}
                  onFileRemove={this.handleFileRemove('cfs_pickup')}
                  title={
                    BOOKING_ACCESSORIALS_MAP.cfs_pickup.localizeDescription
                  }
                  docs={this.state.cfs_pickup_docs}
                  onUpload={(accessorialDocument) => {
                    this.setState((prevState) => {
                      return {
                        accessorialDocumentsCfsPickup: [
                          ...prevState.accessorialDocumentsCfsPickup,
                          accessorialDocument,
                        ],
                      };
                    });
                  }}
                />
              </div>
            )}
            {this.state.cfs_delivery && (
              <div className="mb-4">
                <AccessorialUpload
                  atmeRequired={false}
                  onFileUpload={this.handleFileUpload('cfs_delivery')}
                  onFileRemove={this.handleFileRemove('cfs_delivery')}
                  title={
                    BOOKING_ACCESSORIALS_MAP.cfs_delivery.localizeDescription
                  }
                  docs={this.state.cfs_delivery_docs}
                  onUpload={(accessorialDocument) => {
                    this.setState((prevState) => {
                      return {
                        accessorialDocumentsCfsDelivery: [
                          ...prevState.accessorialDocumentsCfsDelivery,
                          accessorialDocument,
                        ],
                      };
                    });
                  }}
                />
              </div>
            )}
          </div>
          {this.renderAccessorialDetailErrors()}
          {this.renderStepButtons(this.handleAccessorialsSubmit)}
        </AccordionSection>
      </div>
    );
  }

  markers(stops) {
    const filtered = stops.filter((s) => {
      return !s.empty;
    });
    return filtered.map((s) => {
      return {
        coords: {
          lat: s.location[0].lat,
          lng: s.location[0].lng,
        },
        title: s.location[0].address || '',
      };
    });
  }

  _googleMapRef(m) {
    if (m) {
      this._map = m;
      const bounds = new google.maps.LatLngBounds();
      this.state.stops.forEach((stop) => {
        if (stop.location[0].lat && stop.location[0].lng) {
          bounds.extend({
            lat: stop.location[0].lat,
            lng: stop.location[0].lng,
          });
        }
      });
      this._map.fitBounds(bounds);
    }
  }

  renderBookButton() {
    const { booking } = this.state;
    const { t } = this.props;
    return (
      <button
        className="button button-green button-with-icon"
        disabled={booking || !this.valid()}
        onClick={this.submitBooking}
      >
        {!booking && [
          <span key="book-1">{t('BOOK')}</span>,
          <CircleCheckIcon key="book-2" />,
        ]}

        {booking && <LoadingCircle text={t('BOOKING_DOTS')} />}
      </button>
    );
  }

  renderReview() {
    const { stops } = this.state;
    const { t } = this.props;
    const mapHeight = '500px';
    const markers = this.markers(stops);
    return (
      <div className="mt-4">
        <AccordionSection
          title={t('REVIEW_AND_BOOK')}
          stepNumber={stepNumbers.review}
          onHeaderClick={this.getHandleHeaderClick(stepNumbers.review)}
          disabled={!this.state.completedSteps.routeDetails}
          isActive={this.state.step === stepNumbers.review}
        >
          <div style={{ height: mapHeight }}>
            <CargomaticMap
              getMapComponent={(m) => this._googleMapRef(m)}
              defaultZoom={10}
              height={mapHeight}
              markers={markers}
            />
          </div>
          <div className="mt-4 form-actions space-x-4">
            <button
              className="button button-gray button-with-icon"
              onClick={this.previousStep}
            >
              <CircleArrowLeftIcon />
              <span>{titleize(t('BACK'))}</span>
            </button>
            {this.renderBookButton()}
          </div>
        </AccordionSection>
      </div>
    );
  }

  renderStopsSummary() {
    const lastIndex = this.state.stops.length - 1;
    const timezone = this.getActiveTimezone();
    return (
      <div>
        {this.state.stops.map((s, i) => {
          const isFirst = i === 0;
          const isLast = i === lastIndex;

          return (
            <StopSummaryItem
              stop={s}
              key={i}
              timezone={timezone}
              isFirst={isFirst}
              isLast={isLast}
            />
          );
        })}
      </div>
    );
  }

  renderLoadsSummary() {
    const { t } = this.props;
    const { full_truckload, loads, weight, weight_units } = this.state;
    const className = getClassName({
      'items-center': full_truckload || loads.length <= 1,
      'mb-2': true,
    });
    return (
      <IconTextDisplay className={className} icon={<TruckIcon />}>
        {full_truckload && (
          <span>
            {t('FULL_TRUCKLOAD_WITH_WEIGHT', {
              weight,
              weight_units: weight_units || DEFAULT_WEIGHT_UNIT,
            })}
          </span>
        )}
        {!full_truckload && (
          <div className="space-y-2">
            {loads.map((l, i) => (
              <div key={i} className="">
                {getLoadText(l, t)}
              </div>
            ))}
          </div>
        )}
      </IconTextDisplay>
    );
  }

  renderReferenceNumberSummary() {
    const { reference_numbers: refnums } = this.state;
    if (refnums.length === 0) {
      return null;
    }
    const { t } = this.props;
    return (
      <IconTextDisplay icon={<ClipboardListIcon />}>
        <div className="flex flex-wrap -mx-2">
          {refnums
            .filter((refnum) => !refnum.stop_id)
            .map((refnum, i) => {
              return (
                <dl className="px-2 mb-2 text-left" key={i}>
                  <dt className="text-sm text-gray-500 form-medium">
                    {t(sluggify(refnum?.name))}
                  </dt>
                  <dd
                    className="text-sm text-gray-900 truncate"
                    title={refnum.value}
                  >
                    {refnum.value}
                  </dd>
                </dl>
              );
            })}
        </div>
      </IconTextDisplay>
    );
  }

  renderSummary() {
    const { t, localeData } = this.props;
    const shipperName = this.state.shipper
      ? unescapeHTML(this.state.shipper.company_name)
      : t('NOT_SELECTED');

    return (
      <div className="h-full lg:col-span-4 lg:sticky lg:top-0 lg:left-0">
        <CargomaticCard
          className="lg:sticky lg:top-0"
          header={() => (
            <div className="flex items-center">
              <h3 className="my-0">{t('SHIPMENT_SUMMARY')}</h3>
              <div className="h-8" />
            </div>
          )}
        >
          {this.state.offer && (
            <CargomaticAlert
              type="success"
              canDelete
              className="mb-4"
              onDelete={() => this.setState({ offer: null })}
            >
              <Trans
                i18nKey="SHIPMENT_SUCCESSFULLY_CREATED"
                values={{ shipmentId: this.state.offer.shipment_id }}
                components={[
                  <Link
                    id="new-offer-link"
                    to={getShipmentDetailUrl(this.state.offer._id)}
                  />,
                ]}
              />
            </CargomaticAlert>
          )}
          {this.state.booking_error && (
            <CargomaticAlert
              type="error"
              title={t('SHIPMENT_BOOKING_ERROR')}
              className="mb-4"
            >
              <div key="error">{this.state.booking_error}</div>
            </CargomaticAlert>
          )}
          <div className="space-y-4">
            {SessionStore.isAdmin() && (
              <IconTextDisplay
                className="items-center mb-2"
                icon={<UserIcon />}
                text={shipperName}
              />
            )}
            {this.renderLoadsSummary()}
            {this.renderReferenceNumberSummary()}
            {this.renderStopsSummary()}
          </div>
          <hr />
          {this.state.showFSCDisclaimer && <FSCDisclaimer />}
          <div className="flex items-center justify-between">
            <div className="text-lg font-medium">
              <DistanceUnitFormatter
                fromUnit="m"
                toUnit={localeData.distance_units}
                distance={this.state.distance}
              />
            </div>
            <div className="text-xl font-medium text-green">
              {formatMoney(this.state.price || 0, DEFAULT_CURRENCY)}
            </div>
          </div>
        </CargomaticCard>
        {this.state.showDebug && this.renderDebug()}
      </div>
    );
  }

  toggleDebug() {
    this.setState((prevState) => {
      return {
        showDebug: !prevState.showDebug,
      };
    });
  }

  renderDebug() {
    const state = JSON.stringify(this.state, null, 2);
    const { t } = this.props;
    return (
      <CargomaticCard>
        <div className="flex flex-row">
          <h4>{t('DEBUG')}</h4>
          <div
            className="pl-2 mt-3 cursor-pointer"
            onClick={this.toggleDebug.bind(this)}
          >
            <i
              className={this.state.showDebug ? 'fa fa-minus' : 'fa fa-plus'}
            />
          </div>
        </div>
        {this.state.showDebug && <pre>{state}</pre>}
      </CargomaticCard>
    );
  }

  renderDetails() {
    return (
      <div className="lg:col-span-8">
        <div className="overflow-x-hidden overflow-y-auto">
          {SessionStore.isAdmin() && this.renderDispatch()}
          {this.renderLoads()}
          {this.renderAccessorials()}
          {this.renderRoute()}
          {this.renderReview()}
        </div>
      </div>
    );
  }

  render() {
    const bookingClass = getClassName('grid', 'lg:grid-cols-12', 'lg:gap-6', {
      'accounting-hold':
        SessionStore.isShipper() && this.state.hasAccountingHold,
    });
    return (
      <Container className="py-8">
        {this.renderConfirmDeleteLocationModal()}
        {this.renderAccountingHoldModal()}
        {this.renderAddRefnumsModal()}
        <div className={bookingClass}>
          {this.renderDetails()}
          {this.renderSummary()}
        </div>
      </Container>
    );
  }
}

const TimezoneSelect = (props) => {
  const items = props.timezones.filter((o) => typeof o === 'object');
  const { t } = useTranslation();
  return (
    <CargomaticSelect
      items={items}
      valueKey="abbr"
      textKey="name"
      showCaption
      caption={t('SELECT_TIMEZONE')}
      onSelect={(item) => props.onTimezoneChange(item.abbr)}
      selected={props.selectedTimezone}
    />
  );
};

export default withTranslation()(withLocaleContext(Booking));
